import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Chip, Tooltip, CircularProgress, Alert, Snackbar, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

// Mismas listas usadas en la pantalla de catálogo de exámenes (Insumos y reactivos)
const INSUMO_UNITS = ['mL', 'L', 'mg', 'g', 'µL', 'UI', 'unidad', 'tira', 'tubo', 'lanceta', 'otro'];
const INSUMO_TYPES = ['Reactivo', 'Insumo', 'Consumible', 'Equipo/Kit'];

const fmt = (n) => Number(n || 0).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const emptySupply = () => ({
  code: '',
  name: '',
  type: 'Reactivo',
  unit: 'mL',
  precioNeto: '',
  stock: '',
  active: true,
});

export default function SuppliesCatalog() {
  const { filterByBranch } = useBranch();
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, supply: null });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadSupplies = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'labSupplies'), orderBy('name')));
      setSupplies(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    } catch (err) {
      // Fallback sin orderBy en caso de que falten índices o campos
      const snap = await getDocs(collection(db, 'labSupplies'));
      setSupplies(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSupplies();
  }, []);

  const openNew = () => setDialog({ open: true, supply: emptySupply() });
  const openEdit = (supply) => setDialog({
    open: true,
    supply: {
      ...supply,
      code: supply.code || '',
      name: supply.name || '',
      type: supply.type || 'Reactivo',
      unit: supply.unit || 'mL',
      precioNeto: supply.precioNeto ?? '',
      stock: supply.stock ?? '',
      active: supply.active !== false,
    },
  });

  const closeDialog = () => setDialog({ open: false, supply: null });

  const setField = (field, value) =>
    setDialog((d) => ({ ...d, supply: { ...d.supply, [field]: value } }));

  const handleSave = async () => {
    const supply = dialog.supply;
    if (!supply.name.trim()) return setSnackbar({ open: true, message: 'El nombre del insumo es obligatorio.', severity: 'warning' });
    if (!supply.code.trim()) return setSnackbar({ open: true, message: 'El código del insumo es obligatorio.', severity: 'warning' });
    setSaving(true);
    try {
      const data = {
        code: supply.code.trim(),
        name: supply.name.trim(),
        type: supply.type || 'Reactivo',
        unit: supply.unit || 'mL',
        precioNeto: parseFloat(String(supply.precioNeto).replace(',', '.')) || 0,
        stock: parseFloat(String(supply.stock).replace(',', '.')) || 0,
        active: supply.active !== false,
        updatedAt: serverTimestamp(),
      };
      if (supply.id) {
        await updateDoc(doc(db, 'labSupplies', supply.id), data);
      } else {
        await addDoc(collection(db, 'labSupplies'), { ...data, createdAt: serverTimestamp() });
      }
      setSnackbar({ open: true, message: `Insumo ${supply.id ? 'actualizado' : 'creado'} correctamente.`, severity: 'success' });
      closeDialog();
      loadSupplies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este insumo del catálogo?')) return;
    await deleteDoc(doc(db, 'labSupplies', id));
    setSnackbar({ open: true, message: 'Insumo eliminado.', severity: 'info' });
    loadSupplies();
  };

  const filtered = supplies.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.code || '').toLowerCase().includes(q) ||
      (s.type || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Inventory2Icon sx={{ color: LAB_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Catálogo de Insumos</Typography>
            <Typography variant="caption" color="text.secondary">{supplies.length} insumos registrados</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700, borderRadius: 2 }}>
          Nuevo Insumo
        </Button>
      </Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <TextField
          fullWidth size="small" placeholder="Buscar por código, nombre o tipo..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }}
        />
      </Paper>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : supplies.length === 0 ? (
        <Alert severity="info">No hay insumos registrados. Crea el primero.</Alert>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: LAB_PRIMARY }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }} align="center">Unidad</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }} align="right">Precio Neto</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }} align="right">Stock</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} sx={{ '&:hover': { bgcolor: '#F0F9FF' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: LAB_TEAL }}>{s.code || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: LAB_PRIMARY }}>{s.name}</TableCell>
                    <TableCell>
                      <Chip label={s.type || '—'} size="small"
                        sx={{ bgcolor: '#FFF7ED', color: '#C2410C', fontWeight: 700, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{s.unit || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: LAB_TEAL }}>
                      ${fmt(s.precioNeto)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={fmt(s.stock)} size="small"
                        sx={{
                          bgcolor: Number(s.stock || 0) > 0 ? '#DCFCE7' : '#FEE2E2',
                          color: Number(s.stock || 0) > 0 ? '#16A34A' : '#DC2626',
                          fontWeight: 700, fontSize: '0.75rem',
                        }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(s)} sx={{ color: LAB_PRIMARY }}>
                          <EditIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => handleDelete(s.id)} sx={{ color: '#DC2626' }}>
                          <DeleteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No se encontraron insumos que coincidan con la búsqueda.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {/* Dialog crear/editar */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: LAB_PRIMARY, color: '#fff', fontWeight: 700 }}>
          {dialog.supply?.id ? 'Editar Insumo' : 'Nuevo Insumo'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {dialog.supply && (
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField fullWidth size="small" label="Código *"
                  value={dialog.supply.code} onChange={(e) => setField('code', e.target.value)} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 8
                }}>
                <TextField fullWidth size="small" label="Nombre *"
                  value={dialog.supply.name} onChange={(e) => setField('name', e.target.value)} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField fullWidth select size="small" label="Tipo"
                  value={dialog.supply.type} onChange={(e) => setField('type', e.target.value)}>
                  {INSUMO_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField fullWidth select size="small" label="Unidad"
                  value={dialog.supply.unit} onChange={(e) => setField('unit', e.target.value)}>
                  {INSUMO_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField fullWidth size="small" type="number" label="Precio Neto"
                  value={dialog.supply.precioNeto} onChange={(e) => setField('precioNeto', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  inputProps={{ min: 0 }} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField fullWidth size="small" type="number" label="Stock"
                  value={dialog.supply.stock} onChange={(e) => setField('stock', e.target.value)}
                  inputProps={{ min: 0, step: 0.1 }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} sx={{ color: 'text.secondary' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700 }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
